import { Assets, Texture } from 'pixi.js';

export type AssetKind = 'background' | 'character' | 'evidence' | 'gallery' | 'ui' | 'bgm' | 'se';

export interface AssetReference {
  id: string;
  kind: AssetKind;
  path?: string;
}

export interface ImageAssetResult {
  id: string;
  kind: Extract<AssetKind, 'background' | 'character' | 'evidence' | 'gallery' | 'ui'>;
  texture: Texture;
  placeholder: boolean;
  url: string;
}

export interface AudioAssetResult {
  id: string;
  kind: Extract<AssetKind, 'bgm' | 'se'>;
  placeholder: boolean;
  url: string;
}

export interface MissingAssetWarning {
  id: string;
  kind: AssetKind;
  url: string;
  reason: string;
}

export interface AssetLoaderDebugState {
  warningCount: number;
  warnings: MissingAssetWarning[];
  warningCountsByKind: Record<AssetKind, number>;
  availableAssetCounts: Record<AssetKind, number>;
  availableAssetIds: Record<AssetKind, string[]>;
}

const IMAGE_EXTENSIONS = ['webp', 'png', 'jpg', 'jpeg'] as const;
const AUDIO_EXTENSIONS = ['mp3', 'ogg', 'wav'] as const;
const ASSET_KINDS: readonly AssetKind[] = [
  'background',
  'character',
  'evidence',
  'gallery',
  'ui',
  'bgm',
  'se'
];
const ASSET_URLS: Record<AssetKind, Record<string, string>> = {
  background: import.meta.glob<string>('../assets/images/backgrounds/*.{png,webp,jpg,jpeg}', {
    eager: true,
    import: 'default',
    query: '?url'
  }),
  character: import.meta.glob<string>('../assets/images/characters/**/*.{png,webp,jpg,jpeg}', {
    eager: true,
    import: 'default',
    query: '?url'
  }),
  evidence: import.meta.glob<string>('../assets/images/evidences/*.{png,webp,jpg,jpeg}', {
    eager: true,
    import: 'default',
    query: '?url'
  }),
  gallery: import.meta.glob<string>('../assets/images/gallery/*.{png,webp,jpg,jpeg}', {
    eager: true,
    import: 'default',
    query: '?url'
  }),
  ui: import.meta.glob<string>('../assets/images/ui/*.{png,webp,jpg,jpeg}', {
    eager: true,
    import: 'default',
    query: '?url'
  }),
  bgm: import.meta.glob<string>('../assets/bgm/*.{mp3,ogg,wav}', {
    eager: true,
    import: 'default',
    query: '?url'
  }),
  se: import.meta.glob<string>('../assets/se/*.{mp3,ogg,wav}', {
    eager: true,
    import: 'default',
    query: '?url'
  })
};

export class AssetLoader {
  private readonly warnings: MissingAssetWarning[] = [];

  public async loadImage(reference: AssetReference): Promise<ImageAssetResult> {
    const kind = normalizeImageKind(reference.kind);
    const url = this.resolveAssetUrl(reference, kind);

    try {
      if (shouldAssertAssetExists(url, kind)) {
        await this.assertAssetExists(url, kind);
      }
      return {
        id: reference.id,
        kind,
        texture: await Assets.load<Texture>(url),
        placeholder: false,
        url
      };
    } catch (error) {
      this.warnMissing(reference.id, kind, url, error);
      return {
        id: reference.id,
        kind,
        texture: Texture.WHITE,
        placeholder: true,
        url
      };
    }
  }

  public async loadAudio(reference: AssetReference): Promise<AudioAssetResult> {
    const kind = normalizeAudioKind(reference.kind);
    const url = this.resolveAssetUrl(reference, kind);

    try {
      if (shouldAssertAssetExists(url, kind)) {
        await this.assertAssetExists(url, kind);
      }
      return {
        id: reference.id,
        kind,
        placeholder: false,
        url
      };
    } catch (error) {
      this.warnMissing(reference.id, kind, url, error);
      return {
        id: reference.id,
        kind,
        placeholder: true,
        url
      };
    }
  }

  public hasWarnings(): boolean {
    return this.warnings.length > 0;
  }

  public getWarnings(): MissingAssetWarning[] {
    return this.warnings.map((warning) => ({ ...warning }));
  }

  public getDebugState(): AssetLoaderDebugState {
    const warnings = this.getWarnings();
    const warningCountsByKind = createAssetKindRecord(() => 0);
    for (const warning of warnings) {
      warningCountsByKind[warning.kind] += 1;
    }

    const availableAssetIds = createAssetKindRecord((kind) => listManifestAssetIds(kind));
    const availableAssetCounts = createAssetKindRecord((kind) => availableAssetIds[kind].length);

    return {
      warningCount: warnings.length,
      warnings,
      warningCountsByKind,
      availableAssetCounts,
      availableAssetIds
    };
  }

  public clearWarnings(): void {
    this.warnings.length = 0;
  }

  public resolveDefaultPath(id: string, kind: AssetKind): string {
    const resolvedUrl = resolveFromManifest(id, kind);
    if (resolvedUrl) {
      return resolvedUrl;
    }

    switch (kind) {
      case 'background':
        return `assets/images/backgrounds/${id}.${IMAGE_EXTENSIONS[0]}`;
      case 'character':
        return `assets/images/characters/${id}.${IMAGE_EXTENSIONS[0]}`;
      case 'evidence':
        return `assets/images/evidences/${id}.${IMAGE_EXTENSIONS[0]}`;
      case 'gallery':
        return `assets/images/gallery/${id}.${IMAGE_EXTENSIONS[0]}`;
      case 'ui':
        return `assets/images/ui/${id}.${IMAGE_EXTENSIONS[0]}`;
      case 'bgm':
        return `assets/bgm/${id}.${AUDIO_EXTENSIONS[0]}`;
      case 'se':
        return `assets/se/${id}.${AUDIO_EXTENSIONS[0]}`;
    }
  }

  private resolveAssetUrl(reference: AssetReference, kind: AssetKind): string {
    const resolvedFromPath = reference.path ? resolvePathFromManifest(reference.path, kind) : null;
    if (resolvedFromPath) {
      return resolvedFromPath;
    }

    return reference.path ?? this.resolveDefaultPath(reference.id, kind);
  }

  private async assertAssetExists(url: string, kind: AssetKind): Promise<void> {
    const response = await fetch(url, { method: 'HEAD' });

    if (!response.ok) {
      throw new Error(`Asset responded with HTTP ${response.status}`);
    }

    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    if (contentType && !isExpectedContentType(contentType, kind)) {
      throw new Error(`Asset responded with unexpected content-type "${contentType}"`);
    }
  }

  private warnMissing(id: string, kind: AssetKind, url: string, error: unknown): void {
    const reason = error instanceof Error ? error.message : String(error);
    const warning = { id, kind, url, reason };
    this.warnings.push(warning);
    console.warn(`[AssetLoader] Missing ${kind} asset "${id}" at ${url}: ${reason}`);
  }
}

function normalizeImageKind(kind: AssetKind): ImageAssetResult['kind'] {
  if (
    kind === 'background' ||
    kind === 'character' ||
    kind === 'evidence' ||
    kind === 'gallery' ||
    kind === 'ui'
  ) {
    return kind;
  }

  throw new Error(`Asset kind "${kind}" is not an image kind.`);
}

function normalizeAudioKind(kind: AssetKind): AudioAssetResult['kind'] {
  if (kind === 'bgm' || kind === 'se') {
    return kind;
  }

  throw new Error(`Asset kind "${kind}" is not an audio kind.`);
}

function resolveFromManifest(id: string, kind: AssetKind): string | null {
  const extensions = kind === 'bgm' || kind === 'se' ? AUDIO_EXTENSIONS : IMAGE_EXTENSIONS;
  const candidateNames = new Set(extensions.map((extension) => `${id}.${extension}`));

  for (const [path, url] of Object.entries(ASSET_URLS[kind])) {
    if (candidateNames.has(path.split('/').at(-1) ?? '')) {
      return url;
    }
  }

  return null;
}

function resolvePathFromManifest(assetPath: string, kind: AssetKind): string | null {
  const normalizedAssetPath = normalizeAssetPath(assetPath);
  if (!normalizedAssetPath) {
    return null;
  }

  for (const [path, url] of Object.entries(ASSET_URLS[kind])) {
    if (normalizeAssetPath(path) === normalizedAssetPath) {
      return url;
    }
  }

  return null;
}

function isManifestResolvedUrl(url: string, kind: AssetKind): boolean {
  return Object.values(ASSET_URLS[kind]).includes(url);
}

function shouldAssertAssetExists(url: string, kind: AssetKind): boolean {
  if (kind === 'bgm' || kind === 'se') {
    return true;
  }

  return !isManifestResolvedUrl(url, kind);
}

function normalizeAssetPath(assetPath: string): string | null {
  const normalizedPath = assetPath
    .split(/[?#]/, 1)[0]
    .replace(/^\/+/, '')
    .replace(/^(\.\.\/)+/, '')
    .replace(/^src\//, '')
    .replace(/^assets\//, '');

  return normalizedPath.length > 0 ? normalizedPath : null;
}

function createAssetKindRecord<T>(createValue: (kind: AssetKind) => T): Record<AssetKind, T> {
  return Object.fromEntries(ASSET_KINDS.map((kind) => [kind, createValue(kind)])) as Record<
    AssetKind,
    T
  >;
}

function listManifestAssetIds(kind: AssetKind): string[] {
  return Object.keys(ASSET_URLS[kind])
    .map((path) => stripKnownExtension(path.split('/').at(-1) ?? '', kind))
    .filter((id) => id.length > 0)
    .sort((a, b) => a.localeCompare(b));
}

function stripKnownExtension(fileName: string, kind: AssetKind): string {
  const extensions = kind === 'bgm' || kind === 'se' ? AUDIO_EXTENSIONS : IMAGE_EXTENSIONS;
  const lowerFileName = fileName.toLowerCase();
  for (const extension of extensions) {
    const suffix = `.${extension}`;
    if (lowerFileName.endsWith(suffix)) {
      return fileName.slice(0, -suffix.length);
    }
  }

  return fileName;
}

function isExpectedContentType(contentType: string, kind: AssetKind): boolean {
  if (
    kind === 'background' ||
    kind === 'character' ||
    kind === 'evidence' ||
    kind === 'gallery' ||
    kind === 'ui'
  ) {
    return contentType.startsWith('image/');
  }

  return contentType.startsWith('audio/') || contentType.startsWith('application/ogg');
}
